import React, { useState } from "react";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import FlightSearchForm from "../../components/flight-search/FlightSearchForm";
import FlightSearchResults from "../../components/flight-search/FlightSearchResults";
import { fareSearchService } from "../../services/fare-search.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";

const ACCENT = "#4f3df5";

// Drop a travel photo at public/assets/images/flight-search-bg.jpg; the gradient shows until then
const HERO_BG = `linear-gradient(180deg, rgba(102,126,234,0.55) 0%, rgba(118,75,162,0.55) 100%),
  url("/assets/images/flight-search-bg.jpg") center / cover no-repeat,
  linear-gradient(135deg, #667eea 0%, #764ba2 100%)`;

const FlightSearch = () => {
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState([]);
  const [lastSearch, setLastSearch] = useState(null);

  const showSwal = (title, text, icon) => withReactContent(Swal).fire({ title, text, icon });

  const handleSearch = async (payload) => {
    try {
      setLoading(true);
      setSearched(true);
      setLastSearch(payload);
      const res = await fareSearchService.search(payload);
      if (res?.success) {
        setResults(Array.isArray(res.resData) ? res.resData : []);
      } else {
        setResults([]);
        toast.error(res?.message);
      }
    } catch (err) {
      setResults([]);
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flight-search-page">
      <style>{pageCss}</style>

      <section className="fs-hero" style={{ background: HERO_BG }}>
        <FlightSearchForm onSearch={handleSearch} loading={loading} />
      </section>

      <section className="mt-4">
        <FlightSearchResults results={results} loading={loading} searched={searched} search={lastSearch} />
      </section>
    </div>
  );
};

const pageCss = `
.flight-search-page { background:#f8f9fe; }
.fs-hero { border-radius:16px; padding:56px 40px 64px; position:relative; }

.fr-panel { background:#fff; border-radius:14px; padding:18px; box-shadow:0 1px 3px rgba(16,24,40,.06); position:sticky; top:80px; }
.fr-panel-title { font-size:15px; font-weight:700; color:#111827; }
.fr-filter-title { font-size:11px; font-weight:700; letter-spacing:.6px; text-transform:uppercase; color:#6b7280; margin-bottom:8px; }
.fr-link { border:none; background:none; color:${ACCENT}; font-size:12px; font-weight:600; padding:0; }
.fr-pill { border:1px solid #e5e7eb; background:#fff; border-radius:8px; font-size:12px; height:34px; color:#374151; }
.fr-pill.active, .fr-slot.active { border-color:${ACCENT}; background:#eef0ff; color:${ACCENT}; font-weight:600; }
.fr-slot { width:100%; border:1px solid #e5e7eb; background:#fff; border-radius:8px; padding:8px 4px; font-size:11px; color:#374151;
  display:flex; flex-direction:column; align-items:center; gap:2px; }
.fr-slot i { font-size:16px; }

.fr-heading { font-size:18px; font-weight:700; color:#111827; }
.fr-muted { font-size:12px; color:#6b7280; }
.fr-sortbar { display:flex; background:#fff; border-radius:12px; overflow:hidden; box-shadow:0 1px 3px rgba(16,24,40,.06); margin-bottom:14px; }
.fr-sort { flex:1; border:none; background:#fff; padding:10px 14px; text-align:left; font-size:13px; font-weight:600; color:#374151;
  border-bottom:3px solid transparent; display:flex; flex-direction:column; gap:2px; }
.fr-sort + .fr-sort { border-left:1px solid #f0f1f4; }
.fr-sort.active { color:${ACCENT}; border-bottom-color:${ACCENT}; background:#f8f9fe; }
.fr-sort-sub { font-size:12px; font-weight:500; color:#6b7280; }

.fr-card { background:#fff; border-radius:14px; box-shadow:0 1px 3px rgba(16,24,40,.06); margin-bottom:14px; border:1px solid transparent; transition:border-color .15s, box-shadow .15s; }
.fr-card:hover { border-color:#c7cdfa; box-shadow:0 6px 18px rgba(79,61,245,.10); }
.fr-main { display:grid; grid-template-columns: 1.2fr 2.4fr 1.1fr; gap:20px; align-items:center; padding:18px 20px 10px; }
.fr-airline { display:flex; align-items:center; gap:12px; min-width:0; }
.fr-logo { width:42px; height:42px; border-radius:8px; object-fit:contain; background:#f8f9fb; flex-shrink:0; }
.fr-logo-fallback { display:flex; align-items:center; justify-content:center; font-weight:700; color:${ACCENT}; font-size:13px; }
.fr-airline-name { font-size:14px; font-weight:700; color:#111827; }
.fr-timeline { display:grid; grid-template-columns: auto 1fr auto; gap:14px; align-items:center; }
.fr-time { font-size:22px; font-weight:700; color:#111827; line-height:1.2; }
.fr-dayshift { color:${ACCENT}; font-size:11px; margin-left:2px; }
.fr-city { font-size:13px; color:#374151; }
.fr-path { text-align:center; min-width:90px; }
.fr-line { position:relative; height:2px; background:#d1d5db; margin:6px 0; }
.fr-line-dot { position:absolute; left:0; top:-3px; width:8px; height:8px; border-radius:50%; background:#d1d5db; }
.fr-line-stop { position:absolute; left:50%; top:-4px; width:10px; height:10px; border-radius:50%; background:#fff; border:2px solid ${ACCENT}; transform:translateX(-50%); }
.fr-line-plane { position:absolute; right:-4px; top:-9px; color:#9ca3af; font-size:14px; transform:rotate(90deg); }
.fr-stops { font-size:12px; font-weight:600; }
.fr-price-col { text-align:right; }
.fr-price { font-size:22px; font-weight:800; color:#111827; }
.fr-details-btn { border:1px solid ${ACCENT}; background:#fff; color:${ACCENT}; border-radius:8px; font-size:12px; font-weight:600; height:32px; padding:0 12px; margin-top:6px; }
.fr-details-btn:hover { background:#eef0ff; }
.fr-tags { display:flex; flex-wrap:wrap; gap:8px; padding:0 20px 14px; }
.fr-tag { font-size:11px; font-weight:600; padding:4px 10px; border-radius:999px; background:#f3f4f6; color:#4b5563; }
.fr-tag-green { background:#ecfdf3; color:#15803d; }
.fr-tag-red { background:#fef2f2; color:#b91c1c; }
.fr-tag-orange { background:#eef0ff; color:#c2410c; }
.fr-details { border-top:1px dashed #e5e7eb; padding:16px 20px; background:#fcfcfd; border-radius:0 0 14px 14px; }
.fr-detail-title { font-size:11px; font-weight:700; letter-spacing:.6px; text-transform:uppercase; color:#6b7280; margin-bottom:6px; }
.fr-detail-text { font-size:13px; color:#374151; }
.fr-table th { font-size:11px; color:#8a9ab5; font-weight:700; text-transform:uppercase; background:#f8f9fe; }
.fr-table td { font-size:13px; }
.fr-empty { background:#fff; border-radius:14px; padding:48px 20px; text-align:center; box-shadow:0 1px 3px rgba(16,24,40,.06); }
.fr-empty-title { font-size:16px; font-weight:700; color:#111827; margin:8px 0 4px; }
.fr-skeleton { height:120px; background:linear-gradient(90deg,#fff 25%,#f3f4f6 50%,#fff 75%); background-size:200% 100%; animation:fr-shimmer 1.2s infinite; }
@keyframes fr-shimmer { 0% { background-position:200% 0; } 100% { background-position:-200% 0; } }
.min-w-0 { min-width:0; }

@media (max-width: 991px) {
  .fs-hero { padding:32px 20px 40px; }
  .fr-panel { position:static; }
}
@media (max-width: 767px) {
  .fs-hero { padding:24px 16px 28px; border-radius:12px; }
  .fr-main { grid-template-columns:1fr; gap:12px; padding:14px 16px 8px; }
  .fr-price-col { text-align:left; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:6px; }
  .fr-tags { padding:0 16px 12px; }
  .fr-time, .fr-price { font-size:19px; }
  .fr-sort { padding:8px 10px; font-size:12px; }
}
`;

export default FlightSearch;
